'use client'

import { AnimatePresence, motion } from 'motion/react'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useMeasure } from 'react-use'
import { useGetUniversitiesQuery } from '@/api/universities/get-universities-query'
import { Input } from '@/components/input/input'
import { LoadingSpinner } from '@/components/loading-spinner'
import { IOption } from '@/typings/option'
import { debounce } from '@/utils/functions/debounce'
import { noOp } from '@/utils/functions/noOp'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'

interface IUniversitySearchProps {
    value?: IOption<string>
    onSelect: (option: IOption<string> | null) => void
    onBlur?: () => void
    placeholder?: string
    noResultsLabel?: string
}

const SEARCH_LIMIT = 8
const DEBOUNCE_MS = 200

export const UniversitySearch = ({
    value,
    onSelect = noOp,
    onBlur,
    placeholder,
    noResultsLabel,
}: IUniversitySearchProps) => {
    const [listRef, { height: listHeight }] = useMeasure<HTMLDivElement>()

    const [searchValue, setSearchValue] = useState('')
    const [debouncedQuery, setDebouncedQuery] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState<number | null>(0)

    // Fetch only while the dropdown is open. An empty query returns the
    // alphabetical head, which gives us a useful list on the initial open.
    const { data, isFetching } = useGetUniversitiesQuery(
        { q: debouncedQuery.trim(), limit: SEARCH_LIMIT },
        { enabled: isOpen }
    )

    const options = useMemo<IOption<string>[]>(
        () =>
            (data?.data ?? []).map((university) => ({
                value: university.id,
                label: university.name,
            })),
        [data]
    )

    const debouncedSetQuery = useMemo(
        () => debounce((nextValue: string) => setDebouncedQuery(nextValue), DEBOUNCE_MS),
        []
    )

    const closeDropdown = useCallback(() => {
        setIsOpen(false)
        setHighlightedIndex(0)
    }, [])

    const selectOption = useCallback(
        (option: IOption<string>) => {
            onSelect(option)
            setSearchValue('')
            setDebouncedQuery('')
            closeDropdown()
            onBlur?.()
        },
        [onSelect, onBlur, closeDropdown]
    )

    const onValueChange = useCallback(
        (nextValue: string) => {
            setSearchValue(nextValue)
            debouncedSetQuery(nextValue)
            setHighlightedIndex(0)
            setIsOpen(true)

            // Clear any committed selection while the user edits the query
            if (value) onSelect(null)
        },
        [value, onSelect, debouncedSetQuery]
    )

    const handleInputClick = useCallback(() => {
        // Clicking a committed selection clears it so the user can search again
        if (value) {
            onSelect(null)
            setSearchValue('')
            setDebouncedQuery('')
        }

        setIsOpen(true)
    }, [value, onSelect])

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (!isOpen) return

            if (event.key === 'Escape') {
                closeDropdown()
                return
            }

            if (event.key === 'Enter' && highlightedIndex !== null) {
                const option = options[highlightedIndex]

                if (option) {
                    event.preventDefault()
                    selectOption(option)
                }
            }

            if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                event.preventDefault()

                const optionsCount = options.length
                if (optionsCount === 0) return

                setHighlightedIndex((previous) => {
                    if (previous === null) return event.key === 'ArrowUp' ? optionsCount - 1 : 0

                    return event.key === 'ArrowUp'
                        ? (previous - 1 + optionsCount) % optionsCount
                        : (previous + 1) % optionsCount
                })
            }
        },
        [isOpen, highlightedIndex, options, selectOption, closeDropdown]
    )

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [handleKeyDown])

    return (
        <div className="relative flex w-full">
            <Input
                autoComplete="new-password"
                type="search"
                isLoading={isFetching}
                value={value ? value.label : searchValue}
                onValueChange={onValueChange}
                onClick={handleInputClick}
                onBlur={onBlur}
                placeholder={placeholder}
                containerClassName="w-full"
            />

            <AnimatePresence>
                {isOpen && (
                    <Fragment>
                        <div
                            onClick={closeDropdown}
                            className="fixed left-0 right-0 top-0 bottom-0 min-h-dvh min-w-[100vw] z-[1]"
                        />

                        <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: listHeight || undefined }}
                            exit={{ opacity: 0 }}
                            transition={{
                                opacity: { duration: 0.05, ease: 'easeInOut' },
                                height: {
                                    duration: 0.3,
                                    type: 'spring',
                                    stiffness: 400,
                                    damping: 40,
                                },
                            }}
                            className="bg-white absolute top-[56px] w-full overflow-hidden rounded-l shadow-custom z-[2]"
                        >
                            {/* listRef must stay padding-free: useMeasure reports the
                                content box (excludes padding), so measuring a padded div
                                would under-report the height and clip the bottom. */}
                            <div ref={listRef} className="w-full">
                                <div className="w-full py-2">
                                    {run(() => {
                                        if (!options.length) {
                                            if (isFetching) {
                                                return (
                                                    <div className="flex h-[56px] items-center justify-center px-5">
                                                        <LoadingSpinner color="black" size={20} />
                                                    </div>
                                                )
                                            }

                                            return (
                                                <div className="flex h-[56px] items-center px-5 text-black/60">
                                                    {noResultsLabel ?? 'No results found'}
                                                </div>
                                            )
                                        }

                                        return options.map((option, index) => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                // Prevent the input blur firing before the click registers
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => selectOption(option)}
                                                className={cn(
                                                    'flex w-full cursor-pointer select-none items-center px-5 py-3 text-left hover:bg-black/5',
                                                    highlightedIndex === index && 'bg-black/10'
                                                )}
                                            >
                                                <p className="text-[14px] font-medium leading-[135%]">
                                                    {option.label}
                                                </p>
                                            </button>
                                        ))
                                    })}
                                </div>
                            </div>
                        </motion.div>
                    </Fragment>
                )}
            </AnimatePresence>
        </div>
    )
}
